import {
  adminGiveawayEntrySchema,
  adminGiveawaySchema,
  giveawayActionKindSchema,
  giveawayAudienceSchema,
  MAX_GIVEAWAY_ACTIONS,
  type AdminGiveaway,
  type AdminGiveawayAction,
  type AdminGiveawayEntry,
  type GiveawayActionKind,
  type GiveawayAudience,
  type GiveawayStatus,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Checkbox,
  type Column,
  DateTimeInput,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
  type StatusTone,
  Table,
  Textarea,
} from '@growup/ui';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { IMAGE_ASPECTS, ImageField, useImagesBusy } from '../media/ImageUpload';
import { isoToLisbonInput, lisbonInputToIso } from '../events/labels';
import { formatDateTime } from '../format';
import { matchesQuery } from '../quickFilter';

export const GIVEAWAY_STATUS_LOOK: Record<GiveawayStatus, { label: string; tone: StatusTone }> = {
  draft: { label: 'Rascunho', tone: 'neutral' },
  published: { label: 'Publicado', tone: 'info' },
  drawn: { label: 'Sorteado', tone: 'success' },
  cancelled: { label: 'Cancelado', tone: 'danger' },
};

export const AUDIENCE_LABEL: Record<GiveawayAudience, string> = {
  associates: 'Só associados',
  members: 'Membros e associados',
  everyone: 'Todas as contas (inclui utilizadores)',
};

export const ACTION_LABEL: Record<GiveawayActionKind, string> = {
  follow_twitch: 'Seguir no Twitch',
  follow_instagram: 'Seguir no Instagram',
  follow_youtube: 'Subscrever no YouTube',
  follow_x: 'Seguir no X (Twitter)',
  secret_code: 'Código secreto',
};

/** Uma acao no formulario (os campos como texto, como os inputs os dao). */
interface ActionDraft {
  key: string;
  id: string | null;
  kind: GiveawayActionKind;
  label: string;
  url: string;
  entries: string;
  code: string;
  validFrom: string;
  validUntil: string;
  done: number;
}

let draftKeys = 0;
const newDraft = (kind: GiveawayActionKind): ActionDraft => ({
  key: `nova-${String((draftKeys += 1))}`,
  id: null,
  kind,
  label: '',
  url: '',
  entries: '1',
  code: '',
  validFrom: '',
  validUntil: '',
  done: 0,
});
const draftOf = (a: AdminGiveawayAction): ActionDraft => ({
  key: a.id,
  id: a.id,
  kind: a.kind,
  label: a.label,
  url: a.url ?? '',
  entries: String(a.entries),
  code: a.code ?? '',
  validFrom: isoToLisbonInput(a.validFrom),
  validUntil: isoToLisbonInput(a.validUntil),
  done: a.done,
});

function statusOf(g: AdminGiveaway): { label: string; tone: StatusTone } {
  if (g.status === 'published' && g.window === 'closed')
    return { label: 'Por sortear', tone: 'warning' };
  return GIVEAWAY_STATUS_LOOK[g.status];
}

const autoSlug = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const COLUMNS: Column<AdminGiveaway>[] = [
  {
    key: 'title',
    header: 'Passatempo',
    cell: (g) => (
      <span className="flex flex-col">
        <span className="font-semibold">{g.title}</span>
        <span className="text-caption text-gu-ink-muted">
          Fecha {formatDateTime(g.closesAt)} · {g.prize} · {AUDIENCE_LABEL[g.audience]}
        </span>
      </span>
    ),
  },
  { key: 'entries', header: 'Participantes', align: 'right', cell: (g) => String(g.entries) },
  {
    key: 'status',
    header: 'Estado',
    align: 'right',
    cell: (g) => <StatusBadge tone={statusOf(g).tone}>{statusOf(g).label}</StatusBadge>,
  },
];

/** Passatempos (RF-A5): everything needs `giveaways.manage` (Gestor de Passatempos). */
export function GiveawaysSection() {
  const [list, setList] = useState<AdminGiveaway[] | null>(null);
  const [selected, setSelected] = useState<string | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getJson('/api/site/giveaways', z.array(adminGiveawaySchema))
      .then(setList)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  const onSaved = (saved: AdminGiveaway) => {
    setList((current) =>
      current === null
        ? [saved]
        : current.some((g) => g.id === saved.id)
          ? current.map((g) => (g.id === saved.id ? saved : g))
          : [saved, ...current],
    );
    setSelected(saved.id);
  };
  const giveaway = list?.find((g) => g.id === selected) ?? null;

  if (selected === 'new' || giveaway) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelected(null);
              load();
            }}
          >
            ← Passatempos
          </Button>
        </div>
        {giveaway ? (
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-display text-title uppercase">{giveaway.title}</h2>
            <StatusBadge tone={statusOf(giveaway).tone}>{statusOf(giveaway).label}</StatusBadge>
          </div>
        ) : null}
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <GiveawayForm key={giveaway?.id ?? 'new'} giveaway={giveaway} onSaved={onSaved} />
          {giveaway ? <DrawPanel giveaway={giveaway} onSaved={onSaved} /> : null}
        </div>
        {giveaway && giveaway.status !== 'draft' ? <EntriesCard giveaway={giveaway} /> : null}
      </div>
    );
  }

  return (
    <Card
      headingLevel={2}
      title="Passatempos"
      actions={
        <Button size="sm" onClick={() => setSelected('new')}>
          Novo passatempo
        </Button>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Table
        caption="Passatempos"
        columns={COLUMNS}
        rows={list ?? []}
        rowKey={(g) => g.id}
        empty={list ? 'Ainda não há passatempos.' : 'A carregar…'}
        onRowClick={(g) => setSelected(g.id)}
      />
    </Card>
  );
}

function GiveawayForm({
  giveaway,
  onSaved,
}: {
  giveaway: AdminGiveaway | null;
  onSaved: (g: AdminGiveaway) => void;
}) {
  const [title, setTitle] = useState(giveaway?.title ?? '');
  const [slug, setSlug] = useState(giveaway?.slug ?? '');
  const [prize, setPrize] = useState(giveaway?.prize ?? '');
  const [summary, setSummary] = useState(giveaway?.summary ?? '');
  const [description, setDescription] = useState(giveaway?.description ?? '');
  const [imageUrl, setImageUrl] = useState<string | null>(giveaway?.imageUrl ?? null);
  const [winners, setWinners] = useState(String(giveaway?.winnersCount ?? 1));
  const [audience, setAudience] = useState<GiveawayAudience>(giveaway?.audience ?? 'associates');
  const [rules, setRules] = useState(giveaway?.rules ?? '');
  const [actions, setActions] = useState<ActionDraft[]>(giveaway?.actions.map(draftOf) ?? []);
  const [referrals, setReferrals] = useState(giveaway ? giveaway.referralEntries !== null : false);
  const [referralEntries, setReferralEntries] = useState(String(giveaway?.referralEntries ?? 1));
  const [referralLimit, setReferralLimit] = useState(String(giveaway?.referralLimit ?? 10));
  const [opensAt, setOpensAt] = useState(isoToLisbonInput(giveaway?.opensAt ?? null));
  const [closesAt, setClosesAt] = useState(isoToLisbonInput(giveaway?.closesAt ?? null));
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const photos = useImagesBusy();
  const locked = giveaway?.status === 'drawn' || giveaway?.status === 'cancelled';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const problems: Record<string, string> = {};
    const closes = lisbonInputToIso(closesAt);
    if (closes === null) problems.closesAt = 'Indique a data e a hora.';
    if (!/^\d+$/.test(winners.trim()) || Number(winners) < 1) {
      problems.winnersCount = 'Um número inteiro, 1 ou mais.';
    }
    if (referrals && (!/^\d+$/.test(referralEntries.trim()) || Number(referralEntries) < 1)) {
      problems.referralEntries = 'Um número inteiro, 1 ou mais.';
    }
    if (!/^\d+$/.test(referralLimit.trim()) || Number(referralLimit) < 1) {
      problems.referralLimit = 'Um número inteiro, 1 ou mais.';
    }
    if (actions.some((a) => !/^\d+$/.test(a.entries.trim()) || Number(a.entries) < 1)) {
      problems.actions = 'As entradas de cada ação são um número inteiro, 1 ou mais.';
    }
    setFields(problems);
    if (Object.keys(problems).length > 0) return;
    const body = {
      slug,
      title,
      prize,
      summary,
      description,
      imageUrl,
      winnersCount: Number(winners),
      audience,
      rules,
      actions: actions.map((a) => ({
        id: a.id,
        kind: a.kind,
        label: a.label,
        url: a.kind === 'secret_code' ? null : a.url.trim() || null,
        entries: Number(a.entries),
        code: a.kind === 'secret_code' ? a.code.trim() || null : null,
        validFrom: lisbonInputToIso(a.validFrom),
        validUntil: lisbonInputToIso(a.validUntil),
      })),
      referralEntries: referrals ? Number(referralEntries) : null,
      referralLimit: Number(referralLimit),
      opensAt: lisbonInputToIso(opensAt),
      closesAt: closes,
    };
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const result = giveaway
        ? await send('PUT', `/api/site/giveaways/${giveaway.id}`, body)
        : await send('POST', '/api/site/giveaways', body);
      onSaved(adminGiveawaySchema.parse(result));
      setSaved(giveaway ? 'Guardado.' : 'Criado como rascunho. Publique quando estiver pronto.');
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={2} title={giveaway ? 'Dados do passatempo' : 'Novo passatempo'}>
      <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
        <fieldset disabled={locked} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Título" error={fields.title}>
              <Input
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (!giveaway) setSlug(autoSlug(e.target.value));
                }}
              />
            </Field>
            <Field
              label="Endereço"
              hint={`growup.gg/passatempos/${slug || '…'}`}
              error={fields.slug}
            >
              <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
            </Field>
            <Field label="Prémio" hint="Numa frase: o que se ganha." error={fields.prize}>
              <Input value={prize} onChange={(e) => setPrize(e.target.value)} />
            </Field>
            <Field
              label="Vencedores"
              hint="Depois de publicar já não muda."
              error={fields.winnersCount}
            >
              <Input
                inputMode="numeric"
                value={winners}
                disabled={giveaway?.status === 'published'}
                onChange={(e) => setWinners(e.target.value)}
              />
            </Field>
            <Field
              label="Quem pode participar"
              hint="Depois de publicar já não muda. Os jovens entram pelo encarregado."
              error={fields.audience}
            >
              <Select
                value={audience}
                disabled={giveaway?.status === 'published'}
                onChange={(e) => setAudience(giveawayAudienceSchema.parse(e.target.value))}
              >
                {giveawayAudienceSchema.options.map((a) => (
                  <option key={a} value={a}>
                    {AUDIENCE_LABEL[a]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Abre em"
              optional
              hint="Vazio = ao publicar. Hora de Lisboa."
              error={fields.opensAt}
            >
              <DateTimeInput value={opensAt} onChange={setOpensAt} />
            </Field>
            <Field
              label="Fecha em"
              hint="Hora de Lisboa. O sorteio faz-se depois."
              error={fields.closesAt}
            >
              <DateTimeInput value={closesAt} onChange={setClosesAt} />
            </Field>
            <ImageField
              label="Imagem"
              optional
              value={imageUrl}
              onChange={setImageUrl}
              aspect={IMAGE_ASPECTS.giveaway}
              onBusyChange={photos.track('imagem')}
              error={fields.imageUrl}
            />
          </div>
          <Field label="Resumo" optional error={fields.summary}>
            <Input value={summary} onChange={(e) => setSummary(e.target.value)} />
          </Field>
          <Field
            label="Descrição"
            optional
            hint="Parágrafos separados por uma linha em branco."
            error={fields.description}
          >
            <Textarea
              rows={6}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <Field
            label="Regulamento"
            optional
            hint="Aparece na página do passatempo, numa secção que abre. Parágrafos separados por uma linha em branco."
            error={fields.rules}
          >
            <Textarea rows={8} value={rules} onChange={(e) => setRules(e.target.value)} />
          </Field>
          <ActionsEditor
            actions={actions}
            onChange={setActions}
            published={giveaway?.status === 'published'}
            error={fields.actions}
          />
          <fieldset
            className="flex flex-col gap-3 border-t border-gu-line pt-4"
            disabled={giveaway?.status === 'published'}
          >
            <legend className="font-sub text-label font-bold uppercase">Convidar amigos</legend>
            <Checkbox
              label="Dar entradas a quem convida amigos"
              checked={referrals}
              onChange={(e) => setReferrals(e.target.checked)}
            />
            <p className="text-caption text-gu-ink-muted">
              Cada participante recebe um link. Conta cada amigo que cria conta durante o
              passatempo, confirma o email e abre o link. Depois de publicar já não muda.
            </p>
            {referrals ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Entradas por amigo" error={fields.referralEntries}>
                  <Input
                    inputMode="numeric"
                    value={referralEntries}
                    onChange={(e) => setReferralEntries(e.target.value)}
                  />
                </Field>
                <Field
                  label="Máximo de amigos por pessoa"
                  hint="Os que passam ficam registados, sem entradas."
                  error={fields.referralLimit}
                >
                  <Input
                    inputMode="numeric"
                    value={referralLimit}
                    onChange={(e) => setReferralLimit(e.target.value)}
                  />
                </Field>
              </div>
            ) : null}
          </fieldset>
        </fieldset>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {saved ? <Notice tone="success">{saved}</Notice> : null}
        {!locked ? (
          <div>
            <Button type="submit" disabled={busy || photos.busy}>
              {giveaway ? 'Guardar' : 'Criar rascunho'}
            </Button>
          </div>
        ) : null}
      </form>
    </Card>
  );
}

/** Publish, draw after closing, replace a winner who gave up, or cancel. */
/** Entradas extra: seguir nas redes (por confiança) e códigos secretos. */
function ActionsEditor({
  actions,
  onChange,
  published,
  error,
}: {
  actions: ActionDraft[];
  onChange: (next: ActionDraft[]) => void;
  published: boolean;
  error: string | undefined;
}) {
  const [kind, setKind] = useState<GiveawayActionKind>('follow_twitch');
  const set = (key: string, patch: Partial<ActionDraft>) =>
    onChange(actions.map((a) => (a.key === key ? { ...a, ...patch } : a)));
  return (
    <fieldset className="flex flex-col gap-3 border-t border-gu-line pt-4">
      <legend className="font-sub text-label font-bold uppercase">Entradas extra</legend>
      <p className="text-caption text-gu-ink-muted">
        Participar dá 1 entrada. Cada ação dá as suas entradas, uma vez por pessoa. Seguir nas redes
        conta quando a pessoa carrega no botão (as redes não deixam confirmar). Os códigos secretos
        dizem-se nos diretos ou vídeos e só aparecem aqui.
      </p>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {actions.map((a) => {
        const fixed = published && a.id !== null;
        return (
          <div
            key={a.key}
            className="grid grid-cols-1 gap-3 rounded border border-gu-line p-3 sm:grid-cols-2"
          >
            <p className="font-semibold sm:col-span-2">
              {ACTION_LABEL[a.kind]}
              {a.done > 0 ? (
                <span className="ml-2 text-caption font-normal text-gu-ink-muted">
                  {a.done === 1 ? '1 pessoa já fez' : `${String(a.done)} pessoas já fizeram`}
                </span>
              ) : null}
            </p>
            <Field
              label={a.kind === 'secret_code' ? 'Nome' : 'Conta'}
              optional
              hint={
                a.kind === 'secret_code'
                  ? 'Ex.: Código do direto de sexta.'
                  : 'Ex.: @growupesports.'
              }
            >
              <Input value={a.label} onChange={(e) => set(a.key, { label: e.target.value })} />
            </Field>
            {a.kind === 'secret_code' ? (
              <Field
                label="Código"
                hint="4 a 40 letras, números ou hífenes. Maiúsculas ou minúsculas, tanto faz."
              >
                <Input value={a.code} onChange={(e) => set(a.key, { code: e.target.value })} />
              </Field>
            ) : (
              <Field label="Link" hint="https://…">
                <Input value={a.url} onChange={(e) => set(a.key, { url: e.target.value })} />
              </Field>
            )}
            <Field label="Entradas" hint={fixed ? 'Depois de publicar já não muda.' : undefined}>
              <Input
                inputMode="numeric"
                value={a.entries}
                disabled={fixed}
                onChange={(e) => set(a.key, { entries: e.target.value })}
              />
            </Field>
            {a.kind === 'secret_code' ? (
              <>
                <Field label="Vale a partir de" optional hint="Hora de Lisboa.">
                  <DateTimeInput
                    value={a.validFrom}
                    onChange={(v) => set(a.key, { validFrom: v })}
                  />
                </Field>
                <Field label="Vale até" optional hint="Ex.: o fim do direto.">
                  <DateTimeInput
                    value={a.validUntil}
                    onChange={(v) => set(a.key, { validUntil: v })}
                  />
                </Field>
              </>
            ) : null}
            {a.done === 0 ? (
              <div className="sm:col-span-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onChange(actions.filter((x) => x.key !== a.key))}
                >
                  Tirar esta ação
                </Button>
              </div>
            ) : null}
          </div>
        );
      })}
      {actions.length < MAX_GIVEAWAY_ACTIONS ? (
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Nova ação">
            <Select
              value={kind}
              onChange={(e) => setKind(giveawayActionKindSchema.parse(e.target.value))}
            >
              {giveawayActionKindSchema.options.map((k) => (
                <option key={k} value={k}>
                  {ACTION_LABEL[k]}
                </option>
              ))}
            </Select>
          </Field>
          <Button variant="secondary" onClick={() => onChange([...actions, newDraft(kind)])}>
            Juntar ação
          </Button>
        </div>
      ) : null}
    </fieldset>
  );
}

function DrawPanel({
  giveaway,
  onSaved,
}: {
  giveaway: AdminGiveaway;
  onSaved: (g: AdminGiveaway) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const base = `/api/site/giveaways/${giveaway.id}`;

  const run = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      onSaved(adminGiveawaySchema.parse(await send('POST', `${base}${path}`, body)));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={2} title="Publicação e sorteio">
      <div className="flex flex-col gap-3">
        {giveaway.status === 'draft' ? (
          <>
            <p className="text-gu-ink-muted">
              Rascunho: ainda não aparece no site. Ao publicar, fica em growup.gg/passatempos e os
              associados podem participar.
            </p>
            <Button disabled={busy} onClick={() => void run('/publish')}>
              Publicar
            </Button>
          </>
        ) : null}
        {giveaway.status === 'published' ? (
          <>
            <p className="text-gu-ink-muted">
              {giveaway.entries} participante{giveaway.entries === 1 ? '' : 's'}.{' '}
              {giveaway.window === 'closed'
                ? 'Fechado: pode sortear.'
                : `O sorteio fica disponível quando fechar (${formatDateTime(giveaway.closesAt)}).`}
            </p>
            <Button
              disabled={busy || giveaway.window !== 'closed' || giveaway.entries === 0}
              onClick={() => void run('/draw')}
            >
              Sortear{' '}
              {giveaway.winnersCount === 1
                ? 'o vencedor'
                : `${String(giveaway.winnersCount)} vencedores`}
            </Button>
          </>
        ) : null}
        {giveaway.winners.length > 0 ? (
          <ol className="flex flex-col gap-2">
            {giveaway.winners.map((w) => (
              <li key={w.position} className="flex flex-col gap-1 border-t border-gu-line pt-2">
                <span className="font-semibold">
                  {w.position}. {w.login}
                  {w.contactLogin !== w.login ? (
                    <span className="block text-caption font-normal text-gu-ink-muted">
                      Encarregado: {w.contactLogin}
                    </span>
                  ) : null}
                </span>
                {w.forfeited ? (
                  <StatusBadge tone="neutral">Substituído{w.note ? `: ${w.note}` : ''}</StatusBadge>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      const note = window.prompt(
                        'Porque é substituído? (opcional)',
                        'não respondeu',
                      );
                      if (note === null) return;
                      void run(`/winners/${String(w.position)}/replace`, {
                        note: note.trim() || null,
                      });
                    }}
                  >
                    Desistiu: sortear suplente
                  </Button>
                )}
              </li>
            ))}
          </ol>
        ) : null}
        {giveaway.status === 'drawn' ? (
          <p className="text-caption text-gu-ink-muted">
            Os vencedores foram avisados por email (o encarregado, no caso de um jovem). No site só
            o próprio vê que ganhou.
          </p>
        ) : null}
        {giveaway.status === 'cancelled' ? (
          <Notice tone="danger" title="Cancelado">
            {giveaway.cancelReason ?? ''}
          </Notice>
        ) : null}
        {giveaway.status === 'draft' || giveaway.status === 'published' ? (
          <div className="flex flex-col gap-2 border-t border-gu-line pt-3">
            <Field label="Motivo do cancelamento">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            <Checkbox
              label="Confirmo: cancelar o passatempo"
              checked={confirmCancel}
              onChange={(e) => setConfirmCancel(e.target.checked)}
            />
            <Button
              variant="secondary"
              disabled={busy || !confirmCancel || reason.trim() === ''}
              onClick={() => void run('/cancel', { reason: reason.trim() })}
            >
              Cancelar passatempo
            </Button>
          </div>
        ) : null}
        {error ? <Notice tone="danger">{error}</Notice> : null}
      </div>
    </Card>
  );
}

const ENTRY_COLUMNS: Column<AdminGiveawayEntry>[] = [
  {
    key: 'login',
    header: 'Participante',
    cell: (e) => (
      <span className="flex flex-col">
        <span className="font-semibold">{e.login}</span>
        {e.enteredByLogin !== e.login ? (
          <span className="text-caption text-gu-ink-muted">Inscrito por {e.enteredByLogin}</span>
        ) : null}
      </span>
    ),
  },
  {
    key: 'invited',
    header: 'Convidou',
    cell: (e) =>
      e.invited.length === 0 ? '—' : <span className="text-caption">{e.invited.join(', ')}</span>,
  },
  { key: 'entries', header: 'Entradas', align: 'right', cell: (e) => String(e.entries) },
  { key: 'when', header: 'Quando', align: 'right', cell: (e) => formatDateTime(e.createdAt) },
];

function EntriesCard({ giveaway }: { giveaway: AdminGiveaway }) {
  const [entries, setEntries] = useState<AdminGiveawayEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  useEffect(() => {
    getJson(`/api/site/giveaways/${giveaway.id}/entries`, z.array(adminGiveawayEntrySchema))
      .then(setEntries)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [giveaway.id, giveaway.entries]);
  return (
    <Card headingLevel={2} title="Participantes">
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {entries && entries.length > 0 ? (
        <Input
          type="search"
          aria-label="Procurar participante"
          placeholder="Procurar login"
          className="max-w-xs"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      ) : null}
      <Table
        caption="Participantes"
        columns={ENTRY_COLUMNS}
        rows={(entries ?? []).filter((e) =>
          matchesQuery(query, e.login, e.enteredByLogin, ...e.invited),
        )}
        rowKey={(e) => e.id}
        empty={entries ? 'Ainda ninguém participou.' : 'A carregar…'}
      />
    </Card>
  );
}
